import { formatRupeeLabel } from '../../../../../display-values/lumpsumPlanAmount'
import type { LumpsumFund } from '../../../../api/common.types'
import type { LumpsumAllocationNoActionFund } from '../../../../api/allocation.types'
import { AllocationMovementToggle, PlanCategoryBucketKey } from '../../../../api/types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { useLumpsumPlansContext } from '../../../LumpsumPlansProvider'
import { AllocationFundCardTrailingLayout } from '../../../lumpsum-ui-components/AllocationFundCardTrailingLayout'
import { PlansFundCard, type PlanFundCardCta } from '../../../lumpsum-ui-components/PlansFundCard'
import { buildAllocationInsightsFromTags } from '../allocationInsightTags'
import { ALLOCATION_SELL_FUND_CARD_PIE_ICON_SVG } from './AllocationSellFundCard'
import { planPercentageLabel } from '../../../../../display-values/lumpsumPlanPercentage'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type AllocationNoActionFundCardProps = {
  fund: LumpsumAllocationNoActionFund
  categoryName: string
  showBottomDivider?: boolean
}

export function AllocationNoActionFundCard({
  fund,
  categoryName,
  showBottomDivider = true
}: AllocationNoActionFundCardProps) {
  const { openIdealPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const isin = fund?.isin ?? ''
  const fundName = fund?.fundName ?? ''
  const screenName = PlansScreen.LumpsumAllocation
  const fundRole = PlansFundRole.NoAction

  const openPlanBottomSheet = () => {
    openIdealPlanRecommendationBottomSheet({
      isin,
      bucketType: PlanCategoryBucketKey.noActionFunds
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-no-change`,
      label: 'No change',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'No change', fundName, fundIsin: isin })
        console.log('No change', isin)
      },
      variant: 'completed'
    },
    {
      id: `${isin}-learn-more`,
      label: 'Learn more',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Learn more', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: fund?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: fund?.fundNature ?? '',
        category: fund?.fundCategory ?? '',
        purchaseMode: fund?.purchaseMode ?? ''
      }}
      topRight={<AllocationNoActionFundCardTrailing fund={fund} />}
      status={{
        rank: fund?.powerRank ?? 0,
        rating: fund?.powerRating ?? ''
      }}
      ctas={ctas}
      insights={buildAllocationInsightsFromTags({
        tags: fund?.tags,
        isin,
        categoryName,
        missedGains: fund?.missedGains
      })}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

export function AllocationNoActionFundCardTrailing({ fund }: { fund: LumpsumFund }) {
  const { allocationMovementToggle } = useLumpsumPlansContext()

  if (allocationMovementToggle === AllocationMovementToggle.FUND_MOVEMENT) {
    return <AllocationNoActionFundMovementTrailing fund={fund} />
  }

  return <AllocationNoActionAllocationMovementTrailing fund={fund} />
}

export function AllocationNoActionFundMovementTrailing({ fund }: { fund: LumpsumFund }) {
  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={`${formatRupeeLabel({ rawAmount: fund?.currentValue ?? 0 })}`}
      secondaryIconHtml={ALLOCATION_NO_ACTION_FUND_CARD_NO_CHANGE_ICON_SVG}
      secondaryText="No change"
    />
  )
}

export function AllocationNoActionAllocationMovementTrailing({ fund }: { fund: LumpsumFund }) {
  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={`${formatRupeeLabel({ rawAmount: fund?.currentValue ?? 0 })}`}
      secondaryIconHtml={ALLOCATION_SELL_FUND_CARD_PIE_ICON_SVG}
      secondaryText={planPercentageLabel({ rawPercentage: fund?.currentAllocationPercentage ?? 0 })}
    />
  )
}

export const ALLOCATION_NO_ACTION_FUND_CARD_NO_CHANGE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M0.000214549 5.9998V11.2496C-0.00215221 11.3471 0.015083 11.4441 0.0508981 11.5348C0.0867132 11.6255 0.140378 11.708 0.208711 11.7776C0.27737 11.8474 0.35917 11.903 0.449401 11.9411C0.539631 11.9792 0.636508 11.9991 0.734452 11.9996H1.50019V5.24983H0.735202C0.637258 5.25032 0.540381 5.2702 0.450151 5.30829C0.35992 5.34639 0.27812 5.40197 0.209461 5.47182C0.140993 5.54132 0.08719 5.62387 0.051245 5.71457C0.0153 5.80527 -0.0020537 5.90226 0.000214549 5.9998ZM11.4608 5.27982C11.2454 5.02919 10.9783 4.82825 10.6778 4.69088C10.3772 4.5535 10.0505 4.48295 9.72004 4.4841H7.74458C7.77458 4.34836 7.80458 4.21411 7.82708 4.08512C8.21332 1.88469 7.82708 1.08522 7.44908 0.639736C7.27725 0.438448 7.06367 0.276964 6.82317 0.166494C6.58267 0.0560243 6.32101 -0.000785389 6.05636 8.20115e-06C5.49863 0.0168179 4.96587 0.235063 4.55664 0.614364C4.14741 0.993664 3.88942 1.50835 3.8304 2.06319C3.53565 3.44239 3.4899 3.56313 3.10441 4.10687L2.52842 4.92359C2.35289 5.17457 2.25793 5.47304 2.25617 5.77931V10.4839C2.25692 10.8829 2.41817 11.2646 2.70317 11.5459C2.98816 11.8271 3.37515 11.9846 3.7779 11.9838H9.21305C9.74997 11.9885 10.271 11.8018 10.6828 11.4573C11.0947 11.1128 11.3705 10.6329 11.4608 10.1037L11.9685 7.10376C12.0234 6.78111 12.0064 6.45031 11.9186 6.13501C11.8308 5.81971 11.6745 5.52769 11.4608 5.27982ZM9.96229 9.85466C9.93173 10.031 9.83956 10.1907 9.70221 10.3054C9.56485 10.4201 9.39124 10.4823 9.2123 10.4809H3.77865V5.77931L4.35389 4.96259C4.89433 4.18082 5.23007 3.276 5.33037 2.33093C5.35737 1.92369 5.52162 1.42421 6.05711 1.49996C6.5926 1.57495 6.48835 2.93691 6.32935 3.82488C6.16776 4.56089 5.95013 5.28347 5.67836 5.9863L9.72079 5.9803C9.94354 5.97955 10.1558 6.0763 10.3005 6.24429C10.3718 6.32724 10.424 6.42488 10.4533 6.53026C10.4826 6.63564 10.4883 6.74618 10.47 6.85402L9.96304 9.85466H9.96229Z" fill="#3D3D3D"/>
</svg>`
